import type { EmployeeStatus, Lookups, Mark, WorkerType } from '@/hooks/hr';

/** One person on a farm's register for one date, as the server sends it. */
export interface RegisterRow {
  employeeId: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  designation: string | null;
  workerType: WorkerType;
  status: EmployeeStatus;
  homeFarm: boolean;
  weeklyOffToday: boolean;
  saved: boolean;
  mark: Mark | null;
  suggestedMark: Mark | null;
  leaveTypeId: string | null;
  overtimeHours: string;
  shiftId: string | null;
  note: string | null;
  fromLeave: string | null;
  lockedBy: string | null;
}

/** What can be changed on a row. */
export interface RowValues {
  mark: Mark | null;
  leaveTypeId: string | null;
  overtimeHours: string;
  note: string;
}

export type LeaveType = Lookups['leaveTypes'][number];

export const MARKS: Mark[] = ['PRESENT', 'ABSENT', 'HALF_DAY', 'LEAVE', 'WEEKLY_OFF', 'HOLIDAY'];

/** Marks on which someone has not worked, so overtime makes no sense. */
export function noWork(mark: Mark | null): boolean {
  return mark === null || mark === 'ABSENT' || mark === 'LEAVE';
}

export function serverValues(r: RegisterRow): RowValues {
  return { mark: r.mark, leaveTypeId: r.leaveTypeId, overtimeHours: r.overtimeHours || '0', note: r.note ?? '' };
}

export function sameValues(a: RowValues, b: RowValues): boolean {
  return (
    a.mark === b.mark &&
    (a.leaveTypeId ?? null) === (b.leaveTypeId ?? null) &&
    Number(a.overtimeHours || 0) === Number(b.overtimeHours || 0) &&
    a.note.trim() === b.note.trim()
  );
}

/** Why a row cannot be changed, or null when it can. */
export function lockReason(row: RegisterRow, canEdit: boolean): string | null {
  if (row.lockedBy) return `Pay run ${row.lockedBy} covers this day`;
  if (row.fromLeave) return `Approved leave ${row.fromLeave}. Cancel the leave to change this day.`;
  if (!canEdit) return 'You can see attendance but not change it';
  return null;
}

/** Daily-wage staff only get the kinds of leave meant for them. */
export function leaveTypesFor(row: RegisterRow, all: LeaveType[]): LeaveType[] {
  return row.workerType === 'DAILY' ? all.filter((t) => t.forDailyWorkers) : all;
}
